/**
 * Per-platform character ceilings for the Concept Workbench's copy variants.
 *
 * ── WHERE THE NUMBERS COME FROM (no new constants were invented) ───────────
 * Two existing sources of truth already hold every number this module needs,
 * and both carry provenance:
 *
 *   • `src/constants/ad-specs.ts`      — PAID ad field limits (Meta, LinkedIn,
 *     Google RSA), each with a `provenance` string saying whether it is
 *     platform-doc or they-assert.
 *   • `src/constants/platform-specs.ts` — ORGANIC caption limits
 *     (`caption.maxChars`) for the eight social platforms, read through
 *     `getPlatformSpec` so the `x → twitter` alias is handled once.
 *
 * A Concept declares where it ships in `project_task_delivery_intents`
 * (`{ intent: 'paid' | 'organic', platform }` — `src/lib/briefs/concept-contract.ts`
 * names that table as the home of "Delivery platform"). So the limit that
 * applies to a variant is a function of (field, intent, platform), never of the
 * platform alone: an Instagram *ad* headline truncates at a different place
 * than an Instagram *post* caption, and an Instagram post has no headline at
 * all.
 *
 * ── WHICH NUMBER IS "THE LIMIT" ───────────────────────────────────────────
 * `AdFieldSpec` carries two: `hardMax` (the platform REJECTS beyond this) and
 * `adviseMax` (the platform TRUNCATES/hides beyond this). The workbench warning
 * is literally "will truncate on Meta", so `adviseMax` is the right number and
 * `hardMax` is the fallback when no advisory number exists. LinkedIn states
 * both in its own words ("Use up to 70 characters to avoid truncation (200
 * character maximum)") — 70 is what a marketer needs to see.
 *
 * Fields with no documented ceiling get NO limit rather than a plausible one.
 * Meta and LinkedIn CTAs are buttons chosen from a fixed provider list, so
 * there is no character budget to report; organic posts have no headline field.
 * Returning `undefined` there means `CopyVariantCard` omits `maxChars`, which
 * is the honest render.
 */

import { platformLabel } from '@/components/ui/platform-preview/types';
import { AD_SPECS, type AdFieldSpec, type AdPlatformId } from '@/constants/ad-specs';
import { getPlatformSpec } from '@/constants/platform-specs';
import { countCharacters } from '@/lib/concept-copy/text-metrics';

/** The three fields the workbench's Content section renders. */
export type CopyField = 'headline' | 'caption' | 'cta';

export const COPY_FIELDS: readonly CopyField[] = ['headline', 'caption', 'cta'];

/** How a field is titled on the card. `CopyVariantCard.field` takes this. */
export const COPY_FIELD_LABELS: Record<CopyField, string> = {
  headline: 'Headline',
  caption: 'Caption',
  cta: 'CTA',
};

export type DeliveryIntent = 'paid' | 'organic';

/** One row of `project_task_delivery_intents`, as this module needs it. */
export interface ConceptDeliveryTarget {
  intent: DeliveryIntent;
  /** Null is real and common — 14 of 32 live rows have no platform on file. */
  platform: string | null;
}

export interface FieldLimit {
  /**
   * Grapheme count at which this platform starts truncating. When `foldChars` is set this is the
   * platform's hard cap (the error line), never the fold.
   */
  maxChars: number;
  /**
   * Where the platform folds the text behind "see more" (LinkedIn intro text: 150 against a 3,000
   * cap). Past it the text is still published whole, so it is a note, not a truncation warning.
   */
  foldChars?: number;
  /** Named in the warning: "will truncate on Meta". */
  platformLabel: string;
  intent: DeliveryIntent;
  /** Verbatim from the spec constant, so an unverified number stays visible. */
  provenance: string;
}

export interface LimitBreach extends FieldLimit {
  characters: number;
  /** How many characters must be cut. Always ≥ 1. */
  overBy: number;
}

const AD_PLATFORM_LABELS: Record<AdPlatformId, string> = {
  meta: 'Meta',
  linkedin: 'LinkedIn',
  google_rsa: 'Google',
};

/**
 * Organic platform → the ad platform whose rules govern paid delivery there.
 *
 * Instagram, Facebook and Threads are all bought through Meta Ads Manager, so
 * they share one rule set. TikTok, X, YouTube and Pinterest have their own ad
 * products that `AD_SPECS` does not describe — they map to nothing, and a
 * caller must treat that as "no paid limits on file" rather than defaulting to
 * Meta's numbers, which would print a truncation warning naming a platform the
 * copy will never run on.
 */
export function toAdPlatform(platform: string | null | undefined): AdPlatformId | null {
  if (!platform) return null;
  const key = platform.toLowerCase().trim();
  if (key === 'meta' || key === 'instagram' || key === 'facebook' || key === 'threads') {
    return 'meta';
  }
  if (key === 'linkedin') return 'linkedin';
  if (key === 'google' || key === 'google_rsa' || key === 'google_search') return 'google_rsa';
  return null;
}

/**
 * The paid spec each field maps onto.
 *
 * `caption` is the workbench's name for the long-form body of the ad — Meta's
 * primary text, LinkedIn's intro text, Google's description. On Meta that means
 * `hookFold` (~125 chars visible before "See more"), which is a truncation
 * point and nothing else: primary text itself has no documented ceiling.
 */
function paidSpec(adPlatform: AdPlatformId, field: CopyField): AdFieldSpec | null {
  if (adPlatform === 'meta') {
    if (field === 'headline') return AD_SPECS.meta.headline;
    if (field === 'caption') return AD_SPECS.meta.hookFold;
    return null;
  }
  if (adPlatform === 'linkedin') {
    if (field === 'headline') return AD_SPECS.linkedin.headline;
    if (field === 'caption') return AD_SPECS.linkedin.commentary;
    return null;
  }
  if (field === 'headline') return AD_SPECS.google_rsa.headline;
  if (field === 'caption') return AD_SPECS.google_rsa.description;
  return null;
}

function specCeiling(spec: AdFieldSpec): number | null {
  if (spec.unit !== 'chars') return null;
  return spec.adviseMax ?? spec.hardMax;
}

/** Every limit that applies to one field, one per declared delivery target. */
export function limitsForField(
  field: CopyField,
  targets: readonly ConceptDeliveryTarget[],
): FieldLimit[] {
  const limits: FieldLimit[] = [];
  const seen = new Set<string>();

  for (const target of targets) {
    if (!target.platform) continue;

    if (target.intent === 'paid') {
      const adPlatform = toAdPlatform(target.platform);
      if (!adPlatform) continue;
      const spec = paidSpec(adPlatform, field);
      if (!spec) continue;
      const ceiling = specCeiling(spec);
      if (ceiling == null) continue;
      const isFold =
        field === 'caption' &&
        spec.unit === 'chars' &&
        spec.hardMax != null &&
        spec.adviseMax != null &&
        spec.adviseMax < spec.hardMax;
      const maxChars = isFold ? (spec.hardMax as number) : ceiling;
      const key = `paid:${adPlatform}:${maxChars}`;
      if (seen.has(key)) continue;
      seen.add(key);
      limits.push({
        maxChars,
        ...(isFold ? { foldChars: spec.adviseMax as number } : {}),
        platformLabel: AD_PLATFORM_LABELS[adPlatform],
        intent: 'paid',
        provenance: spec.provenance,
      });
      continue;
    }

    // Organic. A post has one body of text and no headline or CTA field, so
    // only `caption` has a ceiling to report.
    if (field !== 'caption') continue;
    const spec = getPlatformSpec(target.platform);
    if (!spec) continue;
    const key = `organic:${target.platform.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    limits.push({
      maxChars: spec.caption.maxChars,
      platformLabel: platformLabel(target.platform),
      intent: 'organic',
      provenance: 'src/constants/platform-specs.ts — published caption maximum.',
    });
  }

  return limits;
}

/**
 * The limit a card should print: the tightest one, because that is the first
 * platform the copy breaks on. Ties keep the first declared target, so the
 * warning is stable across renders rather than flipping between two platforms
 * that share a number.
 */
export function tightestLimit(
  field: CopyField,
  targets: readonly ConceptDeliveryTarget[],
): FieldLimit | null {
  const limits = limitsForField(field, targets);
  if (limits.length === 0) return null;
  return limits.reduce((tightest, limit) =>
    limit.maxChars < tightest.maxChars ? limit : tightest,
  );
}

/**
 * Every platform this text will truncate on, and by how much.
 *
 * The boundary is EXCLUSIVE: a variant exactly at `maxChars` fits. LinkedIn's
 * own wording is "use up to 70 characters", so 70 is inside the budget and 71
 * is the first character that disappears.
 */
export function checkFieldLimits(
  text: string,
  field: CopyField,
  targets: readonly ConceptDeliveryTarget[],
): LimitBreach[] {
  const characters = countCharacters(text);
  return limitsForField(field, targets)
    .filter((limit) => characters > limit.maxChars)
    .map((limit) => ({ ...limit, characters, overBy: characters - limit.maxChars }))
    .sort((a, b) => b.overBy - a.overBy);
}
